/**
 * SendBrowser — the Send field plus a channel-scoped slice of the Matrix.
 *
 * Operator ask (2026-08-10): *"at the right hand side of send add an arrow
 * which opens a dropdown list of all the notes/cc/pcs (whichever is selected)
 * on the selected channel. its essentially a section from the matrix where
 * user can see where each 'note' is assigned to (names are displayed). if
 * note/cc/pc has already been assigned show a red row but user can still
 * choose them."*
 *
 * TWO CLAIM SOURCES, KEPT APART. A number can be spoken for by
 *   * the MATRIX - an external device in the rig map (`schema/src/matrix.ts`),
 *     chip `rig`; or
 *   * another CONTROL in this project, chip `here`.
 * They are different problems - the first is "my APC40 already sends this",
 * the second is a genuine double-send inside OPButtons - so a row that is both
 * shows both chips and both names.
 *
 * RED IS A WARNING, NEVER A BLOCK. The Matrix models co-ownership on purpose
 * (its own doc cites PCR#1-1 and TOSC2 deliberately both sending CC 1 ch 13),
 * so a claimed row stays clickable. This is the one place it diverges from
 * `MatrixPicker`, which dims claimed cells instead of reddening them; if the
 * two ever need to agree, this is the newer call.
 *
 * NOTES MATCH BY LETTER, NOT BY NUMBER. The schema is explicit that
 * manufacturers offset by +/-12 and the LETTER is the identity while
 * `noteMidi` is bookkeeping. So the row displays "60 - C3" and looks the
 * Matrix up by letter, while the number is what gets written to the item.
 * Both the sharp and flat spelling are tried, because the rig map is
 * hand-authored and the app itself is inconsistent (`NOTE_NAMES` in
 * autotune/session.ts spells flats; the schema's own example is "F#4").
 *
 * PROGRAM CHANGE IS A FIRST-CLASS SLOT since 2026-08-10. `MatrixSlotKind`
 * gained 'pc' precisely because this browser offered a PC list and the rig
 * map had nothing to put in it. The three namespaces stay apart because the
 * kind is part of `slotKey`: CC 5, note 5 and PC 5 on one channel are three
 * different signals and must never read as a clash.
 */

import { createSignal, Show, For } from 'solid-js';
import { layoutItems, type LayoutItem } from '../layout/store.ts';
import { midiSlotsOf } from '../layout/midiAlloc.ts';
import { matrixLibrary } from '../matrix/store.ts';
import { deviceForClaim } from '@opbuttons/schema';
import { kindLabel } from '../layout/kindLabels.ts';
import { noteLabelFor, noteSpellings } from '../matrix/noteNames.ts';

export type SendSpace = 'cc' | 'note' | 'pc';

interface SendBrowserProps {
  space: SendSpace;
  /** 0-based, as items store it. The Matrix is 1-based; converted here. */
  channel: number;
  value: number;
  /** The item being edited, so it never reports itself as a clash. */
  selfId: string;
  onChange: (n: number) => void;
  /** Opens the full Matrix picker. Absent where the item cannot take a pick
   *  from it (the XY pad binds two CCs): the list still shows. */
  onOpenMatrix?: () => void;
  /** The field's label: "Send" on a Button, "CC" on a fader or knob, "X CC"
   *  on an XY pad (2026-10-15: one way to choose a CC on every kind). */
  label?: string;
}

function itemName(it: LayoutItem): string {
  const named = (it as { name?: string }).name;
  return named && named.trim() ? named.trim() : kindLabel(it.kind);
}

const ALL = Array.from({ length: 128 }, (_, i) => i);

export function SendBrowser(props: SendBrowserProps) {
  const [open, setOpen] = createSignal(false);
  let scrollRef: HTMLDivElement | undefined;

  /** Matrix claims on this channel, keyed by number. Built once per render
   *  pass rather than per row - 128 rows x a linear slot scan is the kind of
   *  thing that only shows up on a rig with a real matrix behind it. */
  const matrixClaims = () => {
    const lib = matrixLibrary();
    const ch = props.channel + 1;                 // items 0-based, matrix 1-based
    const out = new Map<number, string>();
    for (const slot of lib.slots) {
      if (slot.channel !== ch || slot.kind !== props.space) continue;
      const who = slot.claims
        .map((c) => {
          const dev = deviceForClaim(lib, c);
          return dev ? `${dev.name} - ${c.controlLabel}` : c.controlLabel;
        })
        .join(', ');
      if (props.space === 'cc') {
        if (typeof slot.ccNumber === 'number') out.set(slot.ccNumber, who);
      } else if (props.space === 'pc') {
        if (typeof slot.pcNumber === 'number') out.set(slot.pcNumber, who);
      } else {
        const label = (slot.noteLabel ?? '').trim().toUpperCase();
        for (const n of ALL) {
          if (noteSpellings(n).some((s) => s.toUpperCase() === label)) out.set(n, who);
        }
      }
    }
    return out;
  };

  /** Claims from other controls in this project, keyed by number. Uses
   *  `midiSlotsOf` for cc/note so this and the copy-allocator can never
   *  disagree about what a kind claims; PC is handled here because the
   *  allocator deliberately does not treat a program number as allocatable. */
  const projectClaims = () => {
    const out = new Map<number, string>();
    for (const it of layoutItems()) {
      if (it.id === props.selfId) continue;
      if (props.space === 'pc') {
        if (it.kind === 'button' && it.actionType === 'pc' && it.channel === props.channel) {
          out.set(it.program ?? 0, itemName(it));
        }
        continue;
      }
      for (const slot of midiSlotsOf(it)) {
        if (slot.space !== props.space || slot.channel !== props.channel) continue;
        const prev = out.get(slot.number);
        out.set(slot.number, prev ? `${prev}, ${itemName(it)}` : itemName(it));
      }
    }
    return out;
  };

  const claimedCount = () => {
    const m = matrixClaims();
    const p = projectClaims();
    return new Set([...m.keys(), ...p.keys()]).size;
  };

  const spaceLabel = () => (props.space === 'cc' ? 'CC' : props.space === 'note' ? 'Note' : 'Program');

  function toggle(): void {
    const next = !open();
    setOpen(next);
    if (!next) return;
    // Centre the current value AFTER the list exists. Sets scrollTop on the
    // inner scroller rather than calling scrollIntoView, which walks up and
    // scrolls the panel (and the page) mid-animation.
    queueMicrotask(() => {
      const row = scrollRef?.querySelector('[data-cur="true"]') as HTMLElement | null;
      if (scrollRef && row) {
        scrollRef.scrollTop = row.offsetTop - scrollRef.clientHeight / 2 + row.offsetHeight / 2;
      }
    });
  }

  return (
    <div class="sendbrowser">
      <label class="inspector-field sendbrowser-field">
        <span>{props.label ?? 'Send'}</span>
        {/* The number and its browse arrow are one field (2026-10-15), so a
            row with the arrow is the same shape as a row without:
            − [ 60 ⌄ ] +. The − and + go around this host
            (inspectorSteppers.ts). */}
        <span class="sendbrowser-value inspector-step-host">
        <input
          type="number" min="0" max="127"
          value={props.value}
          onInput={(e) => {
            const n = parseInt(e.currentTarget.value, 10);
            props.onChange(Number.isFinite(n) ? Math.max(0, Math.min(127, n)) : 0);
          }}
        />
        <button
          type="button"
          class="sendbrowser-btn"
          data-open={open() ? 'true' : undefined}
          aria-expanded={open() ? 'true' : 'false'}
          onClick={toggle}
          title={`Browse ${spaceLabel()} on channel ${props.channel + 1}`}
        >
          {/* Drawn, not the ▾ character: at this size the glyph rendered as
              a "-" and read as "decrease" beside the field (2026-10-15). */}
          <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true">
            <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        </span>
      </label>

      {/* grid-template-rows 0fr -> 1fr is the only technique that actually
          interpolates to content height; `height:auto` is not animatable and
          a max-height transition has to overshoot to a guess. The inner div
          carries min-height:0 - without it the grid child refuses to shrink
          below its content and nothing moves. */}
      <div class="sendbrowser-wrap" data-open={open() ? 'true' : undefined}>
        <div class="sendbrowser-inner">
          <div class="sendbrowser-list">
            <div class="sendbrowser-head">
              <b>{spaceLabel()}</b> · ch {props.channel + 1}
              <Show when={props.onOpenMatrix}>
              <button
                type="button"
                class="sendbrowser-open"
                onClick={(e) => { e.stopPropagation(); props.onOpenMatrix?.(); }}
              >
                Open Matrix
              </button>
              </Show>
            </div>
            <div class="sendbrowser-legend">
              {claimedCount()} claimed · {128 - claimedCount()} free
            </div>
            <div class="sendbrowser-scroll" data-space={props.space} ref={scrollRef}>
              <For each={ALL}>
                {(n) => {
                  const mtx = () => matrixClaims().get(n);
                  const app = () => projectClaims().get(n);
                  const who = () => [mtx(), app()].filter(Boolean).join(' + ');
                  return (
                    <button
                      type="button"
                      class="sendbrowser-row"
                      data-claimed={who() ? 'true' : undefined}
                      data-cur={props.value === n ? 'true' : undefined}
                      onClick={() => props.onChange(n)}
                    >
                      <span class="sendbrowser-n">
                        {props.space === 'note' ? `${n} · ${noteLabelFor(n)}` : n}
                      </span>
                      <span class="sendbrowser-who" data-free={who() ? undefined : 'true'}>
                        {who() || 'free'}
                      </span>
                      <Show when={mtx()}>
                        <span class="sendbrowser-chip" title="Claimed in the Matrix by an external device">rig</span>
                      </Show>
                      <Show when={app()}>
                        <span class="sendbrowser-chip" title="Another control in this project already sends this">here</span>
                      </Show>
                    </button>
                  );
                }}
              </For>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
